import { genieTools } from "@/lib/genie";

export type ReportToolResult = (opts: {
  state?: "output-available" | "output-error";
  tool: string;
  toolCallId: string;
  output?: unknown;
  errorText?: string;
}) => Promise<void>;

export type GenieToolCall = {
  toolCallId: string;
  toolName: string;
  input: unknown;
  providerMetadata?: Record<string, Record<string, unknown>>;
};

// True when this call must wait for the user's decision.
//
// The backend is the authority: it gated the tool, so it stamps its verdict as
// `noxus.approval_gate` on every emission of the call — "required" until the
// user allows it, "cleared" once a decision or grant lets it run. Not to be
// confused with the sibling `noxus.approval` key (read in approvalGate.ts):
// that one is the persisted decision *object* re-attached to already-executed
// tool parts on reload, for the allowed/denied badge.
//
// The local registry is only a fallback for a missing verdict, and it can no
// longer *open* a gate the backend closed. It used to be the sole source of
// truth, which made this fail-open twice over: no Genie tool module actually
// sets requires_approval, and the registry is page-scoped, so a tool that
// re-registered (or vanished) after Genie navigated read as ungated and ran
// without ever waiting for the card.
export function isGatedForApproval(toolCall: GenieToolCall): boolean {
  const gate = toolCall.providerMetadata?.noxus?.["approval_gate"];
  if (gate === "required") return true;
  if (gate === "cleared") return false;
  return genieTools.get(toolCall.toolName)?.requires_approval === true;
}

// Executes a frontend tool call and reports the result back to the chat.
// A deferred call must always get an output — a silently dropped one dangles
// forever and re-renders as a phantom approval request on reload — so a tool
// we declared to the agent (declaredToolNames) but that is gone from the
// registry (page changed since) reports output-error. Names never declared
// are server-side tools streaming through the same callback: not ours, skip.
export async function runGenieToolCall(
  toolCall: { toolCallId: string; toolName: string; input: unknown },
  report: ReportToolResult,
  declaredToolNames: ReadonlySet<string>
): Promise<void> {
  const result = await genieTools.run(toolCall.toolName, toolCall.input);
  if (!result) {
    if (declaredToolNames.has(toolCall.toolName)) {
      await report({
        state: "output-error",
        tool: toolCall.toolName,
        toolCallId: toolCall.toolCallId,
        errorText: `Tool "${toolCall.toolName}" is not available on the current page.`,
      });
    }
    return;
  }
  await report(
    result.ok
      ? {
          tool: toolCall.toolName,
          toolCallId: toolCall.toolCallId,
          output: result.output,
        }
      : {
          state: "output-error",
          tool: toolCall.toolName,
          toolCallId: toolCall.toolCallId,
          errorText: result.error,
        }
  );
}
